import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { VariableSelector } from "./VariableSelector";
import { VariableInput } from "./VariableInput";
import { PercentageField } from "./PercentageField";
import { CumulStockVarConfig } from "./CumulStockVarConfig";
import { CumulBulletinsConfig } from "./CumulBulletinsConfig";
import { QuestionnaireVariableSelect } from "./QuestionnaireVariableSelect";
import { BaseParametersRenderer } from "./BaseParametersRenderer";

import type {
  SilaeCondition,
  SilaeConditionOperator,
  SilaeConditionType,
  SilaeFormData,
  SilaeVariableType,
  SilaeVariable,
} from "@/types/silae";
import { useSilaeVariables } from "@/hooks/useSilaeVariables";
import type { FormikProps } from "formik";
import { Variable } from "lucide-react";
import { Sigma } from "lucide-react";
import { HandCoins } from "lucide-react";
import { Wallet } from "lucide-react";
import { BanknoteArrowUp } from "lucide-react";
import { Signature } from "lucide-react";
import { IdCardIcon } from "lucide-react";
import { BriefcaseBusiness } from "lucide-react";
import { ClockFading } from "lucide-react";
import { Contact } from "lucide-react";
import { Calendar1 } from "lucide-react";
import { CalendarDays } from "lucide-react";
import { ShieldUser } from "lucide-react";
import { User } from "lucide-react";
import { Columns3Cog } from "lucide-react";
import { SilaeExpressionEditor } from "./SilaeExpressionEditor";

// TYPES ET INTERFACES
interface ConditionItemProps {
  formik: FormikProps<SilaeFormData>;
  conditionIndex: number;
  canDelete: boolean;
  onDelete: () => void;
  className?: string;
}

// CONSTANTES - moved inside component to use translations

// CONDITION_TYPES moved inside component to use translations

/**
 * Détermine si un type doit utiliser les opérateurs de texte
 */
const shouldUseTextOperators = (type: string) => {
  return type === "Str" || type === "string";
};

export const ConditionItem: React.FC<ConditionItemProps> = ({
  formik,
  conditionIndex,
  canDelete,
  onDelete,
  className,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);

  // Hook pour les variables Silae depuis Redux
  const {
    variables: silaeVariables,
    searchVariables,
    findVariable,
  } = useSilaeVariables();

  // Constants with translations
  const LOGICAL_OPERATORS = [
    { value: "=", label: t("wizard.structure.conditionItem.operators.equal") },
    {
      value: "<>",
      label: t("wizard.structure.conditionItem.operators.notEqual"),
    },
    {
      value: ">",
      label: t("wizard.structure.conditionItem.operators.greater"),
    },
    {
      value: ">=",
      label: t("wizard.structure.conditionItem.operators.greaterEqual"),
    },
    { value: "<", label: t("wizard.structure.conditionItem.operators.less") },
    {
      value: "<=",
      label: t("wizard.structure.conditionItem.operators.lessEqual"),
    },
  ];

  const TEXT_LOGICAL_OPERATORS = [
    { value: "=", label: t("wizard.structure.conditionItem.operators.equal") },
    {
      value: "<>",
      label: t("wizard.structure.conditionItem.operators.notEqual"),
    },
  ];

  // Types de conditions avec traductions - Construits à partir des variables Redux
  const CONDITION_TYPES = useMemo(() => {
    // Helper function to create condition type from variable
    const createConditionType = (
      value: string,
      code: string,
      labelKey: string,
      descriptionKey: string,
      icon: React.ReactNode,
      typeOverride?: string,
    ) => {
      const variable = findVariable(code);

      // Utiliser les options depuis Redux si disponibles
      // Si une variable de type "selection" n'a pas d'options, afficher un warning
      if (typeOverride === "selection" && !variable?.options) {
        console.warn(
          `⚠️ Variable "${code}" is of type "selection" but has no options in Redux. Please add options to the database.`,
        );
      }

      return {
        value,
        code,
        label: t(labelKey),
        description: t(descriptionKey),
        icon,
        type: typeOverride || variable?.type || "string",
        options: variable?.options, // Utiliser uniquement les options depuis Redux
      };
    };

    return [
      createConditionType(
        "VAR_SALARY_BASE",
        "bul.salairedebase",
        "wizard.structure.conditionItem.conditionTypes.salaryBase.label",
        "wizard.structure.conditionItem.conditionTypes.salaryBase.description",
        <Wallet className="h-6 w-6" />,
        "double",
      ),
      createConditionType(
        "VAR_SALARY_GROSS",
        "bul.brut",
        "wizard.structure.conditionItem.conditionTypes.salaryGross.label",
        "wizard.structure.conditionItem.conditionTypes.salaryGross.description",
        <BanknoteArrowUp className="h-6 w-6" />,
        "double",
      ),
      createConditionType(
        "VAR_SALARY_NET",
        "bul.netapayer",
        "wizard.structure.conditionItem.conditionTypes.salaryNet.label",
        "wizard.structure.conditionItem.conditionTypes.salaryNet.description",
        <HandCoins className="h-6 w-6" />,
        "double",
      ),
      createConditionType(
        "VAR_CONTRACT_TYPE",
        "sal.s41.g01.00.012.001",
        "wizard.structure.conditionItem.conditionTypes.contractType.label",
        "wizard.structure.conditionItem.conditionTypes.contractType.description",
        <Signature className="h-6 w-6" />,
        "selection",
      ),
      createConditionType(
        "VAR_PROFESSIONAL_STATUS",
        "sal.s41.g01.00.014",
        "wizard.structure.conditionItem.conditionTypes.professionalStatus.label",
        "wizard.structure.conditionItem.conditionTypes.professionalStatus.description",
        <IdCardIcon className="h-6 w-6" />,
        "selection",
      ),
      createConditionType(
        "VAR_EMPLOYEE_CATEGORY",
        "sal.s41.g01.00.015.001",
        "wizard.structure.conditionItem.conditionTypes.employeeCategory.label",
        "wizard.structure.conditionItem.conditionTypes.employeeCategory.description",
        <BriefcaseBusiness className="h-6 w-6" />,
        "selection",
      ),
      createConditionType(
        "VAR_AGE",
        "sal.age",
        "wizard.structure.conditionItem.conditionTypes.age.label",
        "wizard.structure.conditionItem.conditionTypes.age.description",
        <ClockFading className="h-6 w-6" />,
        "int",
      ),
      createConditionType(
        "VAR_SENIORITY",
        "emp.anciennete",
        "wizard.structure.conditionItem.conditionTypes.seniority.label",
        "wizard.structure.conditionItem.conditionTypes.seniority.description",
        <Contact className="h-6 w-6" />,
        "int",
      ),
      createConditionType(
        "VAR_MONTH",
        "bul.mois",
        "wizard.structure.conditionItem.conditionTypes.month.label",
        "wizard.structure.conditionItem.conditionTypes.month.description",
        <CalendarDays className="h-6 w-6" />,
        "selection",
      ),
      createConditionType(
        "VAR_YEAR_CURRENT",
        "bul.annee",
        "wizard.structure.conditionItem.conditionTypes.year.label",
        "wizard.structure.conditionItem.conditionTypes.year.description",
        <Calendar1 className="h-6 w-6" />,
        "int",
      ),
      createConditionType(
        "VAR_IS_EXECUTIVE",
        "bul.cadre",
        "wizard.structure.conditionItem.conditionTypes.isExecutive.label",
        "wizard.structure.conditionItem.conditionTypes.isExecutive.description",
        <ShieldUser className="h-6 w-6" />,
        "boolean",
      ),
      {
        value: "cumulstockvar",
        label: t(
          "wizard.structure.conditionItem.conditionTypes.cumulStockVar.label",
        ),
        description: t(
          "wizard.structure.conditionItem.conditionTypes.cumulStockVar.description",
        ),
        icon: <Sigma className="h-6 w-6" />,
      },
      {
        value: "cumul_bulletins",
        label: t(
          "wizard.structure.conditionItem.conditionTypes.cumul_bulletins.label",
        ),
        description: t(
          "wizard.structure.conditionItem.conditionTypes.cumul_bulletins.description",
        ),
        icon: <CalendarDays className="h-6 w-6" />,
      },
      {
        value: "silae_variable",
        label: t(
          "wizard.structure.conditionItem.conditionTypes.silaeVariable.label",
        ),
        description: t(
          "wizard.structure.conditionItem.conditionTypes.silaeVariable.description",
        ),
        icon: <Variable className="h-6 w-6" />,
      },
      {
        value: "questionnaire_variable",
        label: t(
          "wizard.structure.conditionItem.conditionTypes.questionnaireVariable.label",
        ),
        description: t(
          "wizard.structure.conditionItem.conditionTypes.questionnaireVariable.description",
        ),
        icon: <User className="h-6 w-6" />,
      },
      {
        value: "custom",
        label: t("wizard.structure.conditionItem.conditionTypes.custom.label"),
        description: t(
          "wizard.structure.conditionItem.conditionTypes.custom.description",
        ),
        icon: <Columns3Cog className="h-6 w-6" />,
      },
    ];
  }, [t, findVariable]);

  // Récupérer la condition
  const condition = formik.values.conditions?.[conditionIndex];

  // Helper functions pour accéder aux erreurs de validation
  const getFieldError = (fieldName: string) => {
    const conditionErrors = formik.errors.conditions?.[conditionIndex];
    if (conditionErrors && typeof conditionErrors === "object") {
      return (conditionErrors as Record<string, string>)[fieldName];
    }
    return undefined;
  };

  const getFieldTouched = (fieldName: string) => {
    if (
      !formik.touched.conditions ||
      !Array.isArray(formik.touched.conditions)
    ) {
      return false;
    }
    const conditionTouched = formik.touched.conditions[conditionIndex];
    if (
      conditionTouched &&
      typeof conditionTouched === "object" &&
      !Array.isArray(conditionTouched) &&
      typeof conditionTouched !== "boolean"
    ) {
      return (conditionTouched as Record<string, boolean>)[fieldName];
    }
    return false;
  };

  const filteredVariables = useMemo(() => {
    const filterVariable = (variable: SilaeVariable) => {
      // Exclure les fonctions qui commencent par "call"
      if (
        variable.type === "function" &&
        variable.code.toLowerCase().startsWith("call")
      ) {
        return false;
      }
      return true;
    };

    if (!searchTerm.trim()) {
      return silaeVariables.filter(filterVariable);
    }
    return searchVariables(searchTerm).filter(filterVariable);
  }, [searchTerm, silaeVariables, searchVariables]);

  const frequentlyUsedVariables = useMemo(() => {
    return silaeVariables.filter((variable) => {
      // Exclure les fonctions qui commencent par "call"
      if (
        variable.type === "function" &&
        variable.code.toLowerCase().startsWith("call")
      ) {
        return false;
      }
      return variable.label && variable.isFrequentlyUsed;
    });
  }, [silaeVariables]);

  // Récupérer la variable sélectionnée
  const selectedVariable = useMemo(() => {
    if (!condition?.silaeVariable) return null as SilaeVariable | null;

    // Pour les variables prédéfinies, utiliser le type défini dans CONDITION_TYPES
    if (condition.type?.startsWith("VAR_")) {
      const conditionType = CONDITION_TYPES.find(
        (t) => t.value === condition.type,
      );
      if (conditionType && "code" in conditionType && "type" in conditionType) {
        const foundVar = findVariable(condition.silaeVariable);
        if (foundVar) {
          // Utiliser le type prédéfini au lieu du type de la variable trouvée
          return {
            ...foundVar,
            type: conditionType.type as SilaeVariableType,
            options:
              "options" in conditionType
                ? conditionType.options
                : foundVar.options,
          };
        }
        // Si la variable n'est pas trouvée, créer un objet minimal avec les infos prédéfinies
        return {
          id: `predefined-${condition.silaeVariable}`,
          code: condition.silaeVariable,
          definition: conditionType.description,
          label: conditionType.label.replace(/^[^\s]+ /, ""), // Enlever l'emoji
          type: conditionType.type as SilaeVariableType,
          category: "bulletin" as const,
          isActive: true,
          isFrequentlyUsed: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          displayName: conditionType.label.replace(/^[^\s]+ /, ""),
          categoryLabel: "Bulletin",
          example: null,
          unit: undefined,
          options:
            "options" in conditionType ? conditionType.options : undefined,
        } as SilaeVariable;
      }
    }

    // Pour les variables normales, utiliser la recherche standard
    return findVariable(condition.silaeVariable);
  }, [
    condition?.silaeVariable,
    condition?.type,
    CONDITION_TYPES,
    findVariable,
  ]);

  const updateCondition = (updates: Partial<SilaeCondition>) => {
    if (!condition) return;
    const updatedCondition = { ...condition, ...updates };
    formik.setFieldValue(`conditions[${conditionIndex}]`, updatedCondition);
  };

  const handleVariableSelect = (variable: SilaeVariable) => {
    const updates: Partial<SilaeCondition> = {
      type: "silae_variable",
      silaeVariable: variable.code,
      conditionOperator: "=", // Toujours = par défaut, surtout pour Bool condition
      conditionValue: variable.type === "boolean" ? "true" : "",
    };

    // Initialiser les paramètres de la fonction si elle en a
    if (
      variable.type === "function" &&
      variable.parameters &&
      variable.parameters.length > 0
    ) {
      const initialParameters = variable.parameters
        .filter((param) => !param.isFixed)
        .map((param) => ({
          id: param.id,
          isRequired: param.isRequired || false,
          label: param.label,
          type: param.type,
          includeBulletinCourant: param.includeBulletinCourant,
          shouldIncludeBulletinCourant: false,
          note: param.note,
          placeholder: param.placeholder,
          helper: param.helper,
          isFixed: param.isFixed,
          fixedValue: param.fixedValue,
          options: param.options,
          value: param.type === "Bool" ? "false" : "",
          percentage:
            param.type === "int" || param.type === "double" ? "100" : undefined,
        }));

      (updates as Partial<SilaeCondition>).functionParameters =
        initialParameters;
    }

    updateCondition(updates);
  };

  // Early return après tous les hooks
  if (!condition) return null;

  return (
    <div className={`space-y-4 p-4 border rounded-lg bg-card ${className}`}>
      {/* En-tête avec numéro et bouton de suppression */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs px-2 py-1">
            {conditionIndex + 1}
          </Badge>
          <Label className="text-sm font-medium">
            {t("wizard.structure.conditionItem.condition")}
          </Label>
        </div>
        {canDelete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Sélecteur de type de condition */}
      <div className="space-y-2">
        <Label>{t("wizard.structure.conditionItem.conditionType")}</Label>
        <Select
          value={condition.type || "silae_variable"}
          onValueChange={(value) => {
            if (value === "cumulstockvar") {
              updateCondition({
                type: value,
                cumulStockVarPeriod: "year_current", // Valeur par défaut
                cumulStockVarDateDebut: "Date(1,1,bul.annee)", // Date par défaut pour année courante
                cumulStockVarDateFin: "Date(31,12,bul.annee)", // Date par défaut pour année courante
                cumulStockVarName: "", // Réinitialiser le nom de la variable
                conditionOperator: "=", // Synchroniser avec conditionOperator
                conditionValue: "", // Synchroniser avec conditionValue
                // Réinitialiser les autres champs
                silaeVariable: "",
                conditionPercentage: "100", // Réinitialiser le pourcentage
              });
            } else if (value === "cumul_bulletins") {
              updateCondition({
                type: value,
                cumulBulletinsPeriod: "year_current", // Valeur par défaut
                cumulBulletinsDateDebut: "Date(1,1,bul.annee)", // Date par défaut pour année courante
                cumulBulletinsDateFin: "Date(31,12,bul.annee)", // Date par défaut pour année courante
                cumulBulletinsType: "0", // Valeur par défaut (cumul des salaires de base)
                cumulBulletinsDivisor: "", // Pas de diviseur par défaut
                conditionOperator: "=", // Synchroniser avec conditionOperator
                conditionValue: "", // Synchroniser avec conditionValue
                // Réinitialiser les autres champs
                silaeVariable: "",
                conditionPercentage: "100", // Réinitialiser le pourcentage
                cumulStockVarName: "", // Réinitialiser les champs cumul stock var
              });
            } else if (value.startsWith("VAR_")) {
              // Gérer les variables prédéfinies
              const conditionType = CONDITION_TYPES.find(
                (t) => t.value === value,
              );
              if (
                conditionType &&
                "code" in conditionType &&
                conditionType.code
              ) {
                updateCondition({
                  type: value as SilaeConditionType,
                  silaeVariable: conditionType.code,
                  conditionOperator: "=",
                  conditionValue: "",
                  conditionPercentage: "100", // Réinitialiser le pourcentage
                  // Réinitialiser les autres champs
                  cumulStockVarName: "",
                });
              }
            } else if (value === "questionnaire_variable") {
              updateCondition({
                type: value as SilaeConditionType,
                questionnaireVariableType: undefined,
                questionnaireVariableIndex: "",
                questionnaireVariableLabel: "",
                questionnaireVariablePercentage: "100",
                questionnaireVariableValue: "",
                questionnaireVariableOperator: "=",
                // Réinitialiser les autres champs
                silaeVariable: "",
                conditionOperator: undefined,
                conditionValue: "",
                conditionPercentage: "100", // Réinitialiser le pourcentage
                cumulStockVarName: "",
              });
            } else {
              updateCondition({
                type: value as SilaeConditionType,
                conditionPercentage: "100", // Réinitialiser le pourcentage
              });
            }
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CONDITION_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                <div className="flex items-center gap-2">
                  {type.icon}
                  <div className="flex flex-col items-start">
                    <div className="font-medium">{type.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {type.description}
                    </div>
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Sélecteur de variable SILAE (seulement pour silae_variable ou variables prédéfinies) */}
      {(condition.type === "silae_variable" ||
        condition.type?.startsWith("VAR_")) && (
        <div className="space-y-2">
          {condition.type === "silae_variable" && (
            <VariableSelector
              selectedVariable={selectedVariable as SilaeVariable | null}
              onVariableSelect={handleVariableSelect}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              open={open}
              setOpen={setOpen}
              frequentlyUsedVariables={frequentlyUsedVariables}
              filteredVariables={filteredVariables}
            />
          )}
        </div>
      )}

      {/* Configuration de la variable sélectionnée */}
      {(condition.type === "silae_variable" ||
        condition.type?.startsWith("VAR_")) &&
        selectedVariable && (
          <div className="space-y-4">
            {/* Pourcentage pour les variables numériques */}
            {(selectedVariable as SilaeVariable).type === "int" ||
              ((selectedVariable as SilaeVariable).type === "double" && (
                <PercentageField
                  formik={formik as unknown as FormikProps<SilaeFormData>}
                  fieldName={`conditions[${conditionIndex}].conditionPercentage`}
                  selectedVariable={selectedVariable as SilaeVariable | null}
                />
              ))}
            {(selectedVariable as SilaeVariable).code === "mtpart.xxxx" && (
              <div className="space-y-2">
                <Label>{t("wizard.structure.conditionItem.mtpartValue")}</Label>
                <VariableInput
                  formik={formik as unknown as FormikProps<SilaeFormData>}
                  fieldName={`conditions[${conditionIndex}].mtpartValue`}
                  variableType={"string" as SilaeVariableType}
                  selectedVariable={selectedVariable as SilaeVariable | null}
                  placeholder={t(
                    "wizard.structure.conditionItem.mtpartValuePlaceholder",
                  )}
                  unit={selectedVariable?.unit}
                />
              </div>
            )}

            {/* Paramètres de la fonction si elle en a */}
            {(selectedVariable as SilaeVariable).type === "function" &&
              (selectedVariable as SilaeVariable).parameters &&
              (selectedVariable as SilaeVariable).parameters!.length > 0 && (
                <BaseParametersRenderer
                  formik={formik as unknown as FormikProps<SilaeFormData>}
                  selectedVariable={selectedVariable as SilaeVariable}
                  fieldPrefix={`conditions.${conditionIndex}.functionParameters`}
                />
              )}

            {/* Champs de comparaison - toujours afficher pour les fonctions et variables sans paramètres */}
            {(!(selectedVariable as SilaeVariable).parameters ||
              (selectedVariable as SilaeVariable).type === "function") && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>
                    {selectedVariable.type === "boolean"
                      ? t("wizard.structure.conditionItem.value")
                      : t("wizard.structure.conditionItem.comparisonValue")}
                  </Label>
                  <Badge variant="outline" className="text-xs">
                    {selectedVariable.type === "boolean"
                      ? t("wizard.structure.conditionItem.types.trueFalse")
                      : selectedVariable.type === "int"
                        ? t("wizard.structure.conditionItem.types.int")
                        : selectedVariable.type === "double"
                          ? t("wizard.structure.conditionItem.types.decimal")
                          : selectedVariable.type === "date"
                            ? t("wizard.structure.conditionItem.types.date")
                            : t(
                                "wizard.structure.conditionItem.types.freeText",
                              )}
                  </Badge>
                </div>

                {selectedVariable.type === "boolean" ? (
                  <div className="space-y-2">
                    <VariableInput
                      formik={formik as unknown as FormikProps<SilaeFormData>}
                      fieldName={`conditions[${conditionIndex}].conditionValue`}
                      variableType={selectedVariable.type as SilaeVariableType}
                      selectedVariable={
                        selectedVariable as SilaeVariable | null
                      }
                      unit={selectedVariable?.unit}
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2 items-start">
                      <div className="w-auto">
                        <Select
                          value={condition.conditionOperator || "="}
                          onValueChange={(value) =>
                            updateCondition({
                              conditionOperator: value as
                                | SilaeConditionOperator
                                | undefined,
                            })
                          }
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(shouldUseTextOperators(
                              (selectedVariable.type as SilaeVariableType) ||
                                "",
                            )
                              ? TEXT_LOGICAL_OPERATORS
                              : LOGICAL_OPERATORS
                            ).map((operator) => (
                              <SelectItem
                                key={operator.value}
                                value={operator.value}
                              >
                                {operator.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <VariableInput
                          formik={
                            formik as unknown as FormikProps<SilaeFormData>
                          }
                          fieldName={`conditions[${conditionIndex}].conditionValue`}
                          variableType={
                            selectedVariable.type as SilaeVariableType
                          }
                          selectedVariable={
                            selectedVariable as SilaeVariable | null
                          }
                          unit={selectedVariable?.unit}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

      {/* Configuration CumulStockVar */}
      {condition.type === "cumulstockvar" && (
        <div className="space-y-4">
          <CumulStockVarConfig
            formik={formik as unknown as FormikProps<SilaeFormData>}
            fieldPrefix={`conditions.${conditionIndex}`}
          />

          {/* Condition de comparaison */}
          <div className="space-y-2">
            <Label>
              {t("wizard.structure.conditionItem.comparisonCondition")}
            </Label>
            <div className="flex gap-2 items-start">
              <div className="w-auto">
                <Select
                  value={condition.conditionOperator || "="}
                  onValueChange={(value) =>
                    updateCondition({
                      conditionOperator: value as
                        | SilaeConditionOperator
                        | undefined,
                    })
                  }
                >
                  <SelectTrigger
                    className={`h-9 ${
                      getFieldTouched("conditionOperator") &&
                      getFieldError("conditionOperator")
                        ? "border-red-500 focus:border-red-500"
                        : ""
                    }`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOGICAL_OPERATORS.map((operator) => (
                      <SelectItem key={operator.value} value={operator.value}>
                        {operator.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <input
                  type="number"
                  className={`w-full px-3 py-2 border border-input bg-background rounded-md text-sm ${
                    getFieldTouched("conditionValue") &&
                    getFieldError("conditionValue")
                      ? "border-red-500 focus:border-red-500"
                      : ""
                  }`}
                  placeholder={t("wizard.structure.conditionItem.compareValue")}
                  value={condition.conditionValue || ""}
                  onChange={(e) =>
                    updateCondition({
                      conditionValue: e.target.value, // Synchroniser avec conditionValue
                    })
                  }
                  onBlur={() => {
                    formik.setFieldTouched(
                      `conditions[${conditionIndex}].conditionOperator`,
                      true,
                    );
                    formik.setFieldTouched(
                      `conditions[${conditionIndex}].conditionValue`,
                      true,
                    );
                  }}
                />
              </div>
            </div>
            {/* Affichage des erreurs */}
            {getFieldTouched("conditionOperator") &&
              getFieldError("conditionOperator") && (
                <p className="text-xs text-red-500">
                  {String(getFieldError("conditionOperator"))}
                </p>
              )}
            {getFieldTouched("conditionValue") &&
              getFieldError("conditionValue") && (
                <p className="text-xs text-red-500">
                  {String(getFieldError("conditionValue"))}
                </p>
              )}
            <p className="text-xs text-muted-foreground">
              {t("wizard.structure.conditionItem.cumulWillBeCompared")}
            </p>
          </div>
        </div>
      )}

      {/* Configuration CumulBulletins */}
      {condition.type === "cumul_bulletins" && (
        <div className="space-y-4">
          <CumulBulletinsConfig
            formik={formik as unknown as FormikProps<SilaeFormData>}
            fieldPrefix={`conditions.${conditionIndex}`}
          />

          {/* Condition de comparaison */}
          <div className="space-y-2">
            <Label>
              {t("wizard.structure.conditionItem.comparisonCondition")}
            </Label>
            <div className="flex gap-2 items-start">
              <div className="w-auto">
                <Select
                  value={condition.conditionOperator || "="}
                  onValueChange={(value) =>
                    updateCondition({
                      conditionOperator: value as
                        | SilaeConditionOperator
                        | undefined,
                    })
                  }
                >
                  <SelectTrigger
                    className={`h-9 ${
                      getFieldTouched("conditionOperator") &&
                      getFieldError("conditionOperator")
                        ? "border-red-500 focus:border-red-500"
                        : ""
                    }`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOGICAL_OPERATORS.map((operator) => (
                      <SelectItem key={operator.value} value={operator.value}>
                        {operator.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <input
                  type="number"
                  className={`w-full px-3 py-2 border border-input bg-background rounded-md text-sm ${
                    getFieldTouched("conditionValue") &&
                    getFieldError("conditionValue")
                      ? "border-red-500 focus:border-red-500"
                      : ""
                  }`}
                  placeholder={t("wizard.structure.conditionItem.compareValue")}
                  value={condition.conditionValue || ""}
                  onChange={(e) =>
                    updateCondition({
                      conditionValue: e.target.value,
                    })
                  }
                  onBlur={() => {
                    formik.setFieldTouched(
                      `conditions[${conditionIndex}].conditionOperator`,
                      true,
                    );
                    formik.setFieldTouched(
                      `conditions[${conditionIndex}].conditionValue`,
                      true,
                    );
                  }}
                />
              </div>
            </div>
            {/* Affichage des erreurs */}
            {getFieldTouched("conditionOperator") &&
              getFieldError("conditionOperator") && (
                <p className="text-xs text-red-500">
                  {String(getFieldError("conditionOperator"))}
                </p>
              )}
            {getFieldTouched("conditionValue") &&
              getFieldError("conditionValue") && (
                <p className="text-xs text-red-500">
                  {String(getFieldError("conditionValue"))}
                </p>
              )}
            <p className="text-xs text-muted-foreground">
              {t("wizard.structure.conditionItem.cumulBulletinsWillBeCompared")}
            </p>
          </div>
        </div>
      )}

      {/* Configuration Variable Questionnaire Salarié */}
      {condition.type === "questionnaire_variable" && (
        <div className="space-y-4">
          <QuestionnaireVariableSelect
            formik={formik as unknown as FormikProps<SilaeFormData>}
            sourceFieldPrefix={`conditions[${conditionIndex}]`}
            label={t("wizard.structure.conditionItem.questionnaireVariable")}
            description={t(
              "wizard.structure.conditionItem.questionnaireDescription",
            )}
            showPercentage={true}
            onlyNumericTypes={false}
          />

          {/* Opérateur de comparaison */}
          {condition.questionnaireVariableType &&
            condition.questionnaireVariableIndex && (
              <div className="space-y-2">
                <Label>
                  {t("wizard.structure.conditionItem.comparisonCondition")}
                </Label>
                <div className="flex flex-col xl:flex-row gap-2 items-center">
                  <div className="w-auto">
                    <Select
                      value={condition.questionnaireVariableOperator || "="}
                      onValueChange={(value) =>
                        updateCondition({
                          questionnaireVariableOperator: value as
                            | SilaeConditionOperator
                            | undefined,
                        })
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(condition.questionnaireVariableType === "text" ||
                        condition.questionnaireVariableType ===
                          ("boolean" as SilaeVariableType)
                          ? TEXT_LOGICAL_OPERATORS
                          : LOGICAL_OPERATORS
                        ).map((operator) => (
                          <SelectItem
                            key={operator.value}
                            value={operator.value}
                          >
                            {operator.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <VariableInput
                      formik={formik as unknown as FormikProps<SilaeFormData>}
                      fieldName={`conditions[${conditionIndex}].questionnaireVariableValue`}
                      variableType={
                        condition.questionnaireVariableType === "boolean"
                          ? "boolean"
                          : condition.questionnaireVariableType === "double"
                            ? "double"
                            : condition.questionnaireVariableType === "int"
                              ? "int"
                              : condition.questionnaireVariableType === "date"
                                ? "dynamicDate"
                                : "Str"
                      }
                      placeholder={
                        condition.questionnaireVariableType === "double"
                          ? "ex: 15.5"
                          : condition.questionnaireVariableType === "int"
                            ? "ex: 25"
                            : condition.questionnaireVariableType === "text"
                              ? "ex: COMMERCIAL"
                              : condition.questionnaireVariableType === "date"
                                ? "Ex: Date(1,1,bul.annee)"
                                : "Valeur à comparer"
                      }
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {condition.questionnaireVariableType ===
                  ("boolean" as SilaeVariableType)
                    ? t(
                        "wizard.structure.conditionItem.questionnaireTypes.boolCompare",
                      )
                    : condition.questionnaireVariableType === "date"
                      ? t(
                          "wizard.structure.conditionItem.questionnaireTypes.dateCompare",
                        )
                      : condition.questionnaireVariableType === "double"
                        ? t(
                            "wizard.structure.conditionItem.questionnaireTypes.doubleCompare",
                          )
                        : condition.questionnaireVariableType === "int"
                          ? t(
                              "wizard.structure.conditionItem.questionnaireTypes.intCompare",
                            )
                          : condition.questionnaireVariableType === "text"
                            ? t(
                                "wizard.structure.conditionItem.questionnaireTypes.textCompare",
                              )
                            : t(
                                "wizard.structure.conditionItem.questionnaireTypes.defaultCompare",
                              )}
                </p>
              </div>
            )}
        </div>
      )}
      {condition.type === "custom" && (
        <div className="space-y-4">
          <div className="space-y-4 border rounded-lg p-4 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-amber-900 dark:text-amber-100">
                {t("wizard.structure.conditionItem.customCondition.label")}
              </Label>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {t(
                  "wizard.structure.conditionItem.customCondition.description",
                )}
              </p>
            </div>
            <SilaeExpressionEditor
              formik={formik}
              fieldPrefix={`conditions[${conditionIndex}].conditionValue`}
              placeholder={t(
                "wizard.structure.conditionItem.customCondition.placeholder",
              )}
              height="300px"
              className="border border-border rounded-lg"
            />
            <p className="text-xs text-amber-600 dark:text-amber-400">
              💡 {t("wizard.structure.conditionItem.customCondition.helpText")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
